import { EmptyState } from '@/components/custom/empty-state';
import EndScroll from '@/app/components/endScroll';
import { Loader } from '@/components/custom/loader';
import { ApiClient } from 'jsonapi-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { AiOutlineTrophy } from 'react-icons/ai';
import { FiChevronDown } from 'react-icons/fi';
import InfiniteScroll from 'react-infinite-scroll-component';
import usePermission from '@/hooks/usePermission';
import moment from 'moment';

export function RegistrationsTable({ filters, currentFilters }) {
  const [sortDirection, setSortDirection] = useState('desc');
  const [tableData, setTableData] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const apiSso = process.env.REACT_APP_SSO_API_URL;
  const [isDownloading, setIsDownloading] = useState(false);
  const token = localStorage.getItem('token');

  const client = new ApiClient({
    url: `${apiSso}/api/v1`
  });

  const sorted = tableData.sort((a, b) => {
    if (sortDirection === 'asc') {
      return a['user-count'] - b['user-count'];
    } else {
      return b['user-count'] - a['user-count'];
    }
  });

  const getTableData = async (page = 1, isNewSearch = false) => {
    if (loading) return;
    setLoading(true);
    let url = `/institution_infos?page[number]=${page}&page[size]=25&initial_date=${filters.values['start-date']}&final_date=${filters.values['end-date']}&sort_dir=${sortDirection}`;

    const regionalsWithId = currentFilters.filter(item => item.type === 'regional' && item.value).map(item => item.value);
    const regionalsWithoutId = currentFilters.filter(item => item.type === 'regional' && !item.value).map(item => item.label);

    const institutionsWithInep = currentFilters.filter(item => item.type === 'institution' && item.value).map(item => item.value);
    const institutionsWithoutInep = currentFilters.filter(item => item.type === 'institution' && !item.value).map(item => item.label);
    const series = currentFilters.filter(item => item.type === 'serie').map(item => item.value);

    if (regionalsWithId.length > 0) {
      url += `&regional_code=${regionalsWithId.join(',')}`;
    }
    if (regionalsWithoutId.length > 0) {
      url += `&regional_name=${regionalsWithoutId.join(',')}`;
    }

    if (institutionsWithInep.length > 0) {
      url += `&institution_inep=${institutionsWithInep.join(',')}`;
    }

    if (institutionsWithoutInep.length > 0) {
      url += `&institution_name=${institutionsWithoutInep.join(',')}`;
    }
    if (series.length > 0) {
      url += `&source=${series.join(',')}`;
    }

    try {
      const { data } = await client.fetch(url);
      if (data.length < 25) {
        setHasMore(false);
      }
      setTableData(prev => (isNewSearch ? data : [...prev, ...data]));
    } catch (error) {
      toast.error('Erro ao buscar os dados da tabela.');
    }
    setLoading(false);
  };

  const handleDownload = () => {
    setIsDownloading(true);

    fetch(`https://reports-api.canaleducacao.tv/api/v1/reports/institution_registration_summary`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Erro ao baixar o relatório');
        }
        return response.blob();
      })
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `relatorio_cadastro_${moment().format('YYYYMMDDHHmmss')}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        toast.success('Relatório baixado com sucesso!');
      })
      .catch(error => {
        console.error('Erro no download:', error);
        toast.error('Erro ao baixar o relatório');
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  useEffect(() => {
    setTableData([]);
    setPageNumber(1);
    setHasMore(true);

    getTableData(1, true);
  }, [filters.values, currentFilters, sortDirection]);

  useEffect(() => {
    if (pageNumber > 1) {
      getTableData(pageNumber);
    }
  }, [pageNumber]);

  return (
    <div className="report-widget">
      <div className="report-widget__header">
        <h3 className="report-widget__title">Ranking de Cadastros</h3>
        <div className="report-widget__filter">
          {usePermission('Download de relatórios', 'Relatórios') && (
            <button
              className="btn btn--outline"
              type="button"
              onClick={handleDownload}
            >
              {isDownloading ? 'Baixando...' : 'Download do relatório'}
            </button>
          )}
        </div>
      </div>

      <div className="report-widget__body">
        {tableData.length === 0 && !loading ? (
          <EmptyState
            title="Nenhum dado disponível"
            type="data"
            text="Não há dados a serem exibidos."
          />
        ) : (
          <InfiniteScroll
            dataLength={tableData.length}
            next={() => {
              setPageNumber(pageNumber + 1);
            }}
            hasMore={hasMore}
            loader={<Loader />}
            endMessage={<EndScroll />}
          >
            <table
              className="round-dg round-dg--light"
              style={{ minWidth: '720px' }}
            >
              <thead className="round-dg__header">
                <tr className="round-dg__row">
                  <th className="round-dg__cell-header round-dg__cell-header--sm">GRE</th>
                  <th className="round-dg__cell-header round-dg__cell-header--sm">Instituição</th>
                  <th className="round-dg__cell-header round-dg__cell-header--sm u-text-right">
                    Alunos cadastrados
                    <span
                      onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                      className="card__icon-wrapper card__icon-wrapper--sm u-cursor-pointer ml-2"
                    >
                      <FiChevronDown
                        style={{ transform: sortDirection === 'desc' ? 'rotate(0deg)' : 'rotate(180deg)' }}
                        className="card__icon"
                      />
                    </span>
                  </th>
                  <th
                    className="round-dg__cell-header round-dg__cell-header--sm u-text-right"
                    style={{ width: '176px' }}
                  >
                    Alunos no iSeduc
                  </th>
                </tr>
              </thead>

              <tbody className="round-dg__body">
                {tableData.length > 0 &&
                  sorted.map((item, i) => (
                    <tr
                      key={item.id}
                      className={`round-dg__row`}
                      // onClick={onOpen && item.id !== 0 ? () => onOpen(item) : null}
                    >
                      <th className="round-dg__cell-header round-dg__cell-header--sm round-dg__cell-header--row">
                        {' '}
                        {i === 0 && sortDirection === 'desc' && <AiOutlineTrophy className="card__icon card__icon--golden mr-3" />}
                        {i === 1 && sortDirection === 'desc' && <AiOutlineTrophy className="card__icon card__icon--silver mr-3" />}
                        {i === 2 && sortDirection === 'desc' && <AiOutlineTrophy className="card__icon card__icon--bronze mr-3" />}
                        {item.gre}
                      </th>
                      <td className="round-dg__cell round-dg__cell--sm">
                        <div className="round-dg__user">
                          <div className="round-dg__user-inner">
                            <h3 className="round-dg__title">{item.name}</h3>
                          </div>
                        </div>
                      </td>
                      <td className="round-dg__cell round-dg__cell--sm u-text-right">{item['user-count']}</td>
                      <td className="round-dg__cell round-dg__cell--sm u-text-right">{item['iseduc-count'] ? item['iseduc-count'] : '-'}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
}
